import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  ChevronRight,
  Compass,
  Layers,
  Database,
  Activity,
  Binary,
  Cpu,
  Lock,
  FileCheck2,
  ExternalLink,
  Scale,
  Radar,
  ArrowRight,
} from 'lucide-react';
import PageTransition from '@/components/global/PageTransition';
import SEO from '@/components/global/SEO';
import CaseStudyModal from '@/components/marketing/CaseStudyModal';
import RasterGridBackdrop from '@/components/ui/RasterGridBackdrop';
import { Content, Language } from '@/types';

interface VisionProps {
  content?: Content;
  lang: Language;
}

export const Vision: React.FC<VisionProps> = ({ content, lang }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isRtl = lang === 'ar';

  const t = {
    en: {
      seoTitle: 'The Spatial Truth Manifesto | VEXL Vision & Architecture',
      seoDesc:
        'The foundational manifesto of VEXL: Bridging the Vector-Pixel Duality, solving the Spatial Hallucination Crisis, and establishing Sovereign Geospatial Intelligence.',
      nodeLabel: '30.0444° N, 31.2357° E // CAIRO SOVEREIGN NODE',
      manifestoEyebrow: 'FOUNDATIONAL MANIFESTO // VEXL ARCHITECTURE',
      manifestoTitle: 'The Spatial Truth Manifesto',
      manifestoSubtitle:
        'Geography is not an ambient backdrop. It is the deterministic physical substrate that governs every supply chain, crop harvest, water aquifer, and sovereign economic asset on Earth.',
      tenetsHeader: 'CORE ARCHITECTURAL TENETS',
      tenets: [
        {
          id: 'reliability',
          title: 'Reliability',
          subtitle: 'Deterministic Geodetic Math',
          desc: 'Spatial operations bounded by rigorous ellipsoidal geodesics and topological invariants—never stochastic next-token guessing.',
          code: 'EPSG:4326 ⇄ EPSG:32636 / KARNEY GEODESIC',
        },
        {
          id: 'interpretability',
          title: 'Interpretability',
          subtitle: 'Sensor-to-Pixel Lineage',
          desc: 'Complete mathematical provenance tracing every output back to calibrated top-of-atmosphere radiance, solar azimuths, and raw polygon vertices.',
          code: 'TOA_RADIANCES / L2A_SURFACE_REFLECTANCE',
        },
        {
          id: 'steerability',
          title: 'Steerability',
          subtitle: 'Rigorous Geometric Bounds',
          desc: 'Programmatic execution graphs with deterministic coordinate reference system (CRS) transformations and zero tolerance for positional drift.',
          code: 'GEOM_CLOSURE: 100% / PLANAR_INVARIANTS',
        },
        {
          id: 'sovereignty',
          title: 'Sovereignty',
          subtitle: 'Air-Gapped & Zero-Egress',
          desc: 'Self-contained inference within sovereign national infrastructure. Zero customer telemetry egress, with strict non-training guarantees.',
          code: 'AIR_GAPPED: CLASS-A / EGIPA_DEPOSIT_#5007',
        },
      ],
      telemetryTitle: 'SOVEREIGN NODE TELEMETRY',
      telemetry: [
        { label: 'DATUM', value: 'WGS 84 (EPSG:4326)' },
        { label: 'PROJECTION', value: 'UTM Zone 36N (EPSG:32636)' },
        { label: 'ELLIPSOID', value: 'a=6378137.0m, 1/f=298.257223563' },
        { label: 'AIR-GAP ISOLATION', value: 'ZERO-EGRESS AIR-GAPPED' },
      ],
      // Chapter 1
      ch1Tag: 'CHAPTER 01 // FOUNDATIONAL ONTOLOGY',
      ch1Title: 'The Vector-Pixel Duality: Bridging Discrete Topology and Continuous Satellite Rasters',
      ch1Lead:
        'Modern geospatial software is paralyzed by a fundamental ontological schism: the physical world is simultaneously continuous and discrete.',
      ch1P1:
        'Earth observation sensors capture the continuous physical planet as multi-spectral raster tensors—radiance arrays across discrete wavelengths (NIR, Red-Edge, SWIR, Thermal). Conversely, human institutions, land ownership, administrative borders, and urban networks exist as discrete topological vectors—points, directed edges, and closed planar polygons.',
      ch1P2:
        'Traditional GIS pipelines handle this through clumsy, lossy conversions: polygons are rasterized with edge-aliasing errors, or raster rasters are naively vectorized into millions of jagged, unsimplified vertices. In both directions, sub-pixel physical truth is permanently lost.',
      ch1P3:
        'VEXL resolves this through a Unified Tensor-Topology Runtime. Rather than converting vector to raster or raster to vector, our spatial engine evaluates geometries directly against GPU tensor dimensions. By computing sub-pixel area-weighted fractional integrals across spectral bands, VEXL extracts mathematically exact physical properties from multi-spectral satellite swaths without compromising planar topological invariants.',
      ch1FormulaTitle: 'UNIFIED ZONAL REFLECTANCE INTEGRAL',
      ch1FormulaCode:
        'R_zonal(Ω) = ∬_Ω S(x, y, λ) · W_subpixel(x, y) dx dy  /  ∬_Ω W_subpixel(x, y) dx dy\nConstraints: ∇ · Topology(∂Ω) = 0   [Strict Planar Closure, No Self-Intersection]',
      // Chapter 2
      ch2Tag: 'CHAPTER 02 // COGNITIVE GEOMETRY',
      ch2Title: 'The Spatial Hallucination Crisis: Why Generic LLMs Fail at CRS, Projections & Band Math',
      ch2Lead:
        'Language models are sequential 1D token predictors. They possess no innate sensory experience of Riemannian manifolds or non-Euclidean planetary geodesics.',
      ch2P1:
        'When enterprise software wraps generic LLMs around spatial queries, catastrophic hallucinations inevitably occur. A language model asked to calculate distances between coordinates defaults to spherical Euclidean formulas, completely oblivious to Earth’s oblateness (the WGS84 flattening factor f = 1/298.257223563). Over regional distances, this error compounds into dozens of kilometers.',
      ch2P2:
        'Even more dangerous is CRS (Coordinate Reference System) conflation. Generic models routinely confuse geographic angular degrees (EPSG:4326) with projected planar meters (EPSG:3857, EPSG:32636). A 0.001° error represents approximately 111 meters on the equator—the difference between sovereign territory, farm parcel ownership, and legal compliance.',
      ch2P3:
        'Finally, spectral band mathematics (such as NDVI, NDWI, or EVI) require calibrated Top-of-Atmosphere (TOA) and Surface Reflectance (SR) radiance calibration. Generic LLMs generate plausible-sounding decimal numbers out of thin air, fabricating vegetative health without ever inspecting raw satellite sensors.',
      ch2MatrixHeader: 'SYSTEM COMPARISON MATRIX: SPATIAL INTEGRITY',
      matrixCol1: 'CAPABILITY',
      matrixCol2: 'GENERIC LLM / AI WRAPPERS',
      matrixCol3: 'VEXL SOVEREIGN GEOAI ENGINE',
      matrixRows: [
        {
          cap: 'CRS & Datum Shift Handling',
          bad: 'Hallucinated; mixes degrees & projected meters indiscriminately',
          good: 'Strict EPSG transformation pipeline via PROJ/GDAL bindings',
        },
        {
          cap: 'Geodesic Distance & Area',
          bad: 'Flat planar Euclidean approximations (up to 38% error)',
          good: 'Karney ellipsoidal geodesic integration with sub-millimeter precision',
        },
        {
          cap: 'Spectral Band Processing',
          bad: 'Stochastically guessed decimal values from token embeddings',
          good: 'Deterministically calculated from calibrated multi-spectral arrays',
        },
        {
          cap: 'Execution Verification',
          bad: 'Unverifiable conversational claims with zero spatial artifacts',
          good: 'Cryptographically hashed GeoJSON & Cloud-Optimized GeoTIFF outputs',
        },
      ],
      // Chapter 3
      ch3Tag: 'CHAPTER 03 // NATIONAL INFRASTRUCTURE',
      ch3Title: 'Sovereign Geospatial Intelligence: Air-Gapped Readiness & National Spatial Data Integrity',
      ch3Lead:
        'Spatial data is sovereign national infrastructure. Crop yields, water aquifers, and critical logistics corridors cannot be processed on foreign public clouds.',
      ch3P1:
        'The modern geopolitical reality demands absolute spatial data sovereignty. Transmitting sensitive agricultural production estimates, hydrological aquifer data, or critical national infrastructure coordinates across foreign cloud APIs introduces unacceptable sovereign vulnerability.',
      ch3P2:
        'VEXL is architected for strict air-gapped readiness. The entire intelligence engine—from multi-modal vision-language orchestration to high-throughput PostGIS/GeoParquet kernels—deploys completely offline in air-gapped sovereign enclaves.',
      ch3P3:
        'We enforce an uncompromising Zero-Egress Policy: customer geospatial vector layers and proprietary satellite pipelines never leave on-premise containment, and customer data is mathematically isolated from public model training cycles.',
      ipBadge: 'OFFICIALLY REGISTERED SOVEREIGN IP // EGIPA #5007',
      ipTitle: 'VEXL Sovereign Spatial Engine — Legally Protected Asset',
      ipDesc:
        'Protected under Egyptian Intellectual Property Authority Law No. 82 of 2002 and Law No. 163 of 2023. Registered software architecture and spatial execution runtime.',
      ipVerifyBtn: 'Verify IP Dossier',
      caseStudyBadge: 'SPOTLIGHT CASE STUDY',
      caseStudyBtn: 'Read Technical Case Study',
    },
    ar: {
      seoTitle: 'بيان الحقيقة المكانية | رؤية ومعمارية فكسل',
      seoDesc:
        'البيان التأسيسي لمنظومة فكسل: حل ثنائية المتجه والبكسل، معالجة أزمة الهلوسة المكانية، وترسيخ الذكاء الجيومكاني السيادي.',
      nodeLabel: '30.0444° شمالاً، 31.2357° شرقاً // العقدة السيادية بالقاهرة',
      manifestoEyebrow: 'البيان التأسيسي // معمارية فكسل',
      manifestoTitle: 'بيان الحقيقة المكانية',
      manifestoSubtitle:
        'الجغرافيا ليست خلفية تجريدية محايدة. إنها الركيزة الفيزيائية الحتمية التي تحكم كل سلسلة إمداد ومحصول زراعي وحوض مائي وأصل اقتصادي سيادي على كوكب الأرض.',
      tenetsHeader: 'الركائز المعمارية الجوهرية',
      tenets: [
        {
          id: 'reliability',
          title: 'الموثوقية',
          subtitle: 'رياضيات جيوديسية حتمية',
          desc: 'عمليات مكانية مقيدة بحسابات الجيوديسيا الإهليلجية وثوابت الطوبولوجيا المكانية—بعيداً عن التخمين الاحتمالي للنماذج اللغوية.',
          code: 'EPSG:4326 ⇄ EPSG:32636 / حسابات كارني الجيوديسية',
        },
        {
          id: 'interpretability',
          title: 'قابلية التفسير',
          subtitle: 'تتبع من المستشعر حتى البكسل',
          desc: 'تتبع حسابي كامل يربط كل مؤشر مستنتج بقيم الإشعاع المعايرة للأقمار الصناعية، وزوايا السمت الشمسي، وإحداثيات الرؤوس الأصلية.',
          code: 'إشعاع قمة الغلاف الجوي / انعكاس السطح L2A',
        },
        {
          id: 'steerability',
          title: 'قابلية التوجيه',
          subtitle: 'أطر هندسية صارمة',
          desc: 'مخططات تنفيذ برمجية ذات تحويلات آلية دقيقة لأنظمة الإسناد المكاني (CRS) مع انعدام تام لأي انزياح أو انحراف في الإحداثيات.',
          code: 'انغلاق هندسي: 100% / ثوابت مستوية',
        },
        {
          id: 'sovereignty',
          title: 'السيادة المكانية',
          subtitle: 'نشر معزول وانعدام تسريب',
          desc: 'استدلال ذاتي مستقل داخل البنية التحتية الوطنية السيادية. صفر تسريب للبيانات التشخيصية مع ضمانات تامة بعدم استخدام البيانات للتدريب.',
          code: 'عزل تام: فئة أ / تسجيل ملكية فكرية #5007',
        },
      ],
      telemetryTitle: 'قراءات العقدة السيادية',
      telemetry: [
        { label: 'المرجع الجيوديسي', value: 'WGS 84 (EPSG:4326)' },
        { label: 'نظام الإسقاط', value: 'UTM Zone 36N (EPSG:32636)' },
        { label: 'الإهليلج المرجعي', value: 'a=6378137.0m, 1/f=298.257223563' },
        { label: 'العزل الشبكي', value: 'معزول شبكياً بالكامل (Air-Gapped)' },
      ],
      // Chapter 1
      ch1Tag: 'الفصل الأول // الأصول الوجودية للمكان',
      ch1Title: 'ثنائية المتجه والبكسل: سد الفجوة بين الطوبولوجيا المتقطعة ومصفوفات الأقمار المستمرة',
      ch1Lead:
        'تعاني برمجيات نظم المعلومات الجغرافية الحديثة من انقسام جوهري: العالم المادي مستمر متصل، بينما النظم المؤسسية متقطعة منفصلة.',
      ch1P1:
        'تلتقط مستشعرات مراقبة الأرض الكوكب المادي المستمر كمصفوفات بكسلية متصلة عبر أطوال موجية متباينة (الأشعة تحت الحمراء القريبة، الحافة الحمراء، الأشعة القصيرة، الحرارية). وفي المقابل، فإن المؤسسات البشرية، والملكيات العقارية، والحدود الإدارية، وشبكات البنية التحتية توجد كمتجهات طوبولوجية متقطعة—نقاط، ومسارات موجهة، ومضلعات مستوية مغلقة.',
      ch1P2:
        'تتعامل خطوط المعالجة التقليدية مع هذه الفجوة بتحويلات فجة تفقد الدقة: إما تنقيط المضلعات مع تشوهات حواف شائنة، أو تحويل البكسلات لملايين الرؤوس المتعرجة غير المبسطة. وفي كلا الاتجاهين، تضيع الحقيقة الفيزيائية تحت البكسلية بشكل نهائي.',
      ch1P3:
        'تحل فكسل هذا التناقض عبر «بيئة التشغيل الموحدة للموترات والطوبولوجيا». بدلاً من تحويل المتجهات لبكسلات أو العكس، يقوم محركنا المكاني بتقييم الأشكال الهندسية مباشرة عبر أبعاد موترات كروت الشاشة (GPU Tensors). ومن خلال حساب تكاملات مساحية كسرية للأطياف تحت مستوى البكسل، تستخلص فكسل خصائص فيزيائية دقيقة رياضياً دون المساس بالثوابت الطوبولوجية.',
      ch1FormulaTitle: 'معادلة التكامل النطاقي الموحد للانعكاس',
      ch1FormulaCode:
        'R_zonal(Ω) = ∬_Ω S(x, y, λ) · W_subpixel(x, y) dx dy  /  ∬_Ω W_subpixel(x, y) dx dy\nالقيود: ∇ · Topology(∂Ω) = 0   [انغلاق مستوٍ صارم، انعدام تام للتقاطع الذاتي]',
      // Chapter 2
      ch2Tag: 'الفصل الثاني // الهندسة المعرفية',
      ch2Title: 'أزمة الهلوسة المكانية: لماذا تعجز النماذج اللغوية العامة عن فهم نظم الإسناد والإسقاطات وحسابات الأطياف',
      ch2Lead:
        'النماذج اللغوية العامة هي مجرد أدوات تنبؤ بالرموز النصية أحادية البعد. وهي تفتقر بنيوياً لأي إدراك لحسابات السطوح الريمانية أو الجيوديسيا الكروية لكوكب الأرض.',
      ch2P1:
        'حين تقوم البرمجيات بتغليف النماذج اللغوية العامة لمعالجة الاستعلامات المكانية، تحدث كوارث الهلوسة حتماً. فعندما يُطلب من النموذج حساب المسافة بين نقطتين، فإنه يلجأ لصيغ إقليدية كروية مسطحة متجاهلاً تفلطح الأرض (معامل التفلطح f = 1/298.257223563). وعلى مستوى الأقاليم، يتراكم هذا الخطأ ليصل لعشرات الكيلومترات.',
      ch2P2:
        'والأخطر من ذلك هو الخلط بين أنظمة الإسناد المكاني (CRS). فالنماذج العامة تخلط باستمرار بين الدرجات الجغرافية (EPSG:4326) والأمتار المستوية المسقطة (EPSG:3857, EPSG:32636). وخطأ مقداره 0.001 درجة جغرافية يترجم إلى نحو 111 متراً على الأرض—وهو الفارق بين سيادة الدول، وحدود الحيازات الزراعية، والمسؤولية القانونية.',
      ch2P3:
        'أما في الحسابات الطيفية (مثل NDVI أو NDWI أو EVI)، فإنها تتطلب معايرة دقيقة لإشعاع قمة الغلاف الجوي وانعكاسات السطح. بينما تقوم النماذج اللغوية العامة باختلاق أرقام عشرية تبدو مقنعة من العدم دون تحليل بكسل قمر صناعي واحد.',
      ch2MatrixHeader: 'مصفوفة المقارنة المعمارية: السلامة المكانية',
      matrixCol1: 'القدرة التقنية',
      matrixCol2: 'النماذج اللغوية العامة وتطبيقات الذكاء السطحية',
      matrixCol3: 'محرك فكسل للذكاء الجيومكاني السيادي',
      matrixRows: [
        {
          cap: 'التعامل مع أنظمة الإسناد المكاني (CRS)',
          bad: 'هلوسة مستمرة وخلط عشوائي بين الدرجات والأمتار',
          good: 'مسار تحويل صارم لأنظمة EPSG عبر مكتبات PROJ/GDAL المدمجة',
        },
        {
          cap: 'حساب المسافات والمساحات الجيوديسية',
          bad: 'تقريبات إقليدية مسطحة (أخطاء تصل إلى 38%)',
          good: 'تكامل جيوديسي إهليلجي وفق معادلات كارني بدقة تحت المليمتر',
        },
        {
          cap: 'معالجة النطاقات والأطياف الفضائية',
          bad: 'توليد احتمالي لأرقام عشرية مصطنعة دون قراءة البيانات',
          good: 'حساب حتمي مباشر من مصفوفات الأقمار الصناعية متعددة الأطياف',
        },
        {
          cap: 'التحقق وتدقيق المخرجات',
          bad: 'نصوص حوارية غير قابلة للإثبات وبلا ملفات مكانية حقيقية',
          good: 'مخرجات GeoJSON و Cloud-Optimized GeoTIFF موثقة بتوقيع رقمي',
        },
      ],
      // Chapter 3
      ch3Tag: 'الفصل الثالث // البنية التحتية الوطنية',
      ch3Title: 'الذكاء الجيومكاني السيادي: الجاهزية للعمل المعزول وسلامة البيانات المكانية الوطنية',
      ch3Lead:
        'البيانات المكانية هي بنية تحتية وطنية سيادية. تقديرات المحاصيل، ومخزونات المياه، والممرات اللوجستية الحيوية لا يمكن معالجتها على سحابات أجنبية.',
      ch3P1:
        'يفرض الواقع الجيوسياسي الراهن حتمية الحفاظ على سيادة البيانات المكانية. إن إرسال تقديرات الإنتاج الزراعي الحساسة، أو بيانات الأحواض المائية، أو إحداثيات البنية التحتية عبر واجهات سحابية خارجية يمثل ثغرة أمنية واقتصادية غير مقبولة.',
      ch3P2:
        'صُممت منظومة فكسل للعمل المعزول التام (Air-Gapped). فالمنظومة بأكملها—من وكلاء الذكاء المكاني وحتى محركات PostGIS و GeoParquet عالية الأداء—تُنشر بالكامل محلياً داخل مراكز البيانات السيادية المغلقة دون أي اتصال خارجي.',
      ch3P3:
        'نحن نطبق سياسة صارمة لمنع تسريب البيانات: لا تخرج طبقات العميل الجغرافية أو خطوط معالجة الأقمار الخاصة خارج بنيته المحمية، ولا يتم مطلقاً استخدام بيانات العميل لتدريب أي نماذج عامة.',
      ipBadge: 'ملكية فكرية مسجلة رسمياً // إيداع رقم 5007',
      ipTitle: 'محرك فكسل الجيومكاني السيادي — أصل برمجي محمي قانونياً',
      ipDesc:
        'محمي بموجب قانون حماية الملكية الفكرية المصري رقم 82 لسنة 2002 ورقم 163 لسنة 2023. معمارية برمجية مسجلة وموثقة لدى الجهاز المصري للملكية الفكرية.',
      ipVerifyBtn: 'التحقق من ملف الملكية الفكرية',
      caseStudyBadge: 'دراسة حالة بارزة',
      caseStudyBtn: 'قراءة التقرير التقني الكامل',
    },
  }[lang];

  return (
    <PageTransition>
      <SEO
        title={t.seoTitle}
        description={t.seoDesc}
        lang={lang}
        keywords={[
          'Spatial Truth',
          'Vector Pixel Duality',
          'Spatial Hallucination',
          'Sovereign GeoAI',
          'Air-Gapped GIS',
          'VEXL Architecture',
        ]}
      />

      <div
        className={`min-h-screen bg-[#05070A] text-[#EDF0F1] selection:bg-[#0E93A3]/30 ${
          isRtl ? 'font-arabic' : 'font-sans'
        } relative overflow-hidden`}
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        <RasterGridBackdrop type="type-a" />

        {/* ═══════════════════════════════════════════════════════════════════════
            TWO-COLUMN EDITORIAL SPLIT CONTAINER
        ═══════════════════════════════════════════════════════════════════════ */}
        <div className="max-w-[1400px] mx-auto px-6 pt-28 pb-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">

            {/* ═══════════════════════════════════════════════════════════════════
                LEFT COLUMN (STICKY ON DESKTOP) — MANIFESTO & CORE TENETS
            ═══════════════════════════════════════════════════════════════════ */}
            <aside className="lg:col-span-5 lg:sticky lg:top-28 space-y-8">
              {/* Eyebrow & Manifesto Main Title */}
              <div className="space-y-4">
                <div className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] font-semibold">
                  {t.manifestoEyebrow}
                </div>
                <h1
                  className="text-3xl md:text-5xl lg:text-[46px] font-medium text-[#EDF0F1] tracking-[-0.5px] leading-[1.12]"
                  style={{
                    fontFamily: isRtl
                      ? "'Noto Sans Arabic', 'Cairo', system-ui, -apple-system, sans-serif"
                      : "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif",
                    lineHeight: isRtl ? 1.35 : 1.12,
                  }}
                >
                  {t.manifestoTitle}
                </h1>
                <p className="text-[#9AA5AC] text-sm md:text-base leading-relaxed font-normal">
                  {t.manifestoSubtitle}
                </p>
                <div className="text-[11px] font-mono text-[#64748B] pt-1">
                  // {t.nodeLabel}
                </div>
              </div>

              {/* Core Tenets Stack */}
              <div className="space-y-3 pt-2">
                <div className="text-[10px] font-mono font-bold text-[#66707A] uppercase tracking-[0.2em]">
                  {t.tenetsHeader}
                </div>

                <div className="space-y-2.5">
                  {t.tenets.map((tenet) => (
                    <div
                      key={tenet.id}
                      className="p-4 rounded-xl bg-[#12161A] border border-[#262C31] hover:border-[#0E93A3]/50 transition-all duration-200 group"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-sm font-semibold text-[#EDF0F1] group-hover:text-[#0E93A3] transition-colors">
                          {tenet.title}
                        </h3>
                        <span className="text-[10px] font-mono text-[#0E93A3] bg-[#0E93A3]/10 px-2 py-0.5 rounded border border-[#0E93A3]/20">
                          {tenet.subtitle}
                        </span>
                      </div>
                      <p className="text-xs text-[#9AA5AC] leading-relaxed mb-2.5">
                        {tenet.desc}
                      </p>
                      <div className="text-[10px] font-mono text-[#66707A] border-t border-[#262C31] pt-2 tnum">
                        {tenet.code}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Geodetic Telemetry Panel */}
              <div className="p-4 rounded-xl bg-[#0B0F12] border border-[#262C31] space-y-2">
                <div className="flex items-center justify-between text-[10px] font-mono font-bold text-[#66707A] uppercase tracking-wider pb-1 border-b border-[#262C31]">
                  <span>{t.telemetryTitle}</span>
                  <span className="text-[#0E93A3]">ONLINE</span>
                </div>
                <div className="grid grid-cols-1 gap-1.5 pt-1">
                  {t.telemetry.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-[11px] font-mono">
                      <span className="text-[#66707A]">{item.label}:</span>
                      <span className="text-[#EDF0F1] font-medium tnum">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </aside>

            {/* ═══════════════════════════════════════════════════════════════════
                RIGHT COLUMN — DEEP SCIENTIFIC & ARCHITECTURAL NARRATIVE
            ═══════════════════════════════════════════════════════════════════ */}
            <main className="lg:col-span-7 space-y-16">

              {/* CHAPTER 01: THE VECTOR-PIXEL DUALITY */}
              <section className="p-8 md:p-10 rounded-xl bg-[#12161A] border border-[#262C31] space-y-6">
                <div className="space-y-2">
                  <span className="text-[11px] font-mono font-bold text-[#0E93A3] uppercase tracking-[0.2em]">
                    {t.ch1Tag}
                  </span>
                  <h2
                    className="text-2xl md:text-3xl font-medium text-[#EDF0F1] tracking-[-0.4px] leading-tight"
                    style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
                  >
                    {t.ch1Title}
                  </h2>
                </div>

                <p className="text-base text-[#EDF0F1] leading-relaxed font-medium border-l-2 border-[#0E93A3] pl-4 italic">
                  {t.ch1Lead}
                </p>

                <div className="space-y-4 text-sm text-[#9AA5AC] leading-relaxed">
                  <p>{t.ch1P1}</p>
                  <p>{t.ch1P2}</p>
                  <p>{t.ch1P3}</p>
                </div>

                {/* Mathematical Formula Callout */}
                <div className="p-4 rounded-lg bg-[#05070A] border border-[#262C31] space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-[#0E93A3]">
                      {t.ch1FormulaTitle}
                    </span>
                    <span className="text-[10px] font-mono text-[#66707A]">GEODETIC KERNEL</span>
                  </div>
                  <pre
                    className="font-mono text-xs text-[#EDF0F1] overflow-x-auto whitespace-pre-wrap py-2 leading-relaxed"
                    dir="ltr"
                  >
                    {t.ch1FormulaCode}
                  </pre>
                </div>
              </section>

              {/* CHAPTER 02: THE SPATIAL HALLUCINATION CRISIS */}
              <section className="p-8 md:p-10 rounded-xl bg-[#12161A] border border-[#262C31] space-y-6">
                <div className="space-y-2">
                  <span className="text-[11px] font-mono font-bold text-[#0E93A3] uppercase tracking-[0.2em]">
                    {t.ch2Tag}
                  </span>
                  <h2
                    className="text-2xl md:text-3xl font-medium text-[#EDF0F1] tracking-[-0.4px] leading-tight"
                    style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
                  >
                    {t.ch2Title}
                  </h2>
                </div>

                <p className="text-base text-[#EDF0F1] leading-relaxed font-medium border-l-2 border-[#0E93A3] pl-4 italic">
                  {t.ch2Lead}
                </p>

                <div className="space-y-4 text-sm text-[#9AA5AC] leading-relaxed">
                  <p>{t.ch2P1}</p>
                  <p>{t.ch2P2}</p>
                  <p>{t.ch2P3}</p>
                </div>

                {/* System Comparison Matrix */}
                <div className="space-y-3 pt-2">
                  <div className="text-[11px] font-mono font-bold text-[#66707A] uppercase tracking-wider">
                    {t.ch2MatrixHeader}
                  </div>

                  <div className="overflow-x-auto rounded-lg border border-[#262C31]">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-[#0B0F12] border-b border-[#262C31] text-[10px] font-mono text-[#9AA5AC] uppercase tracking-wider">
                          <th className="p-3 w-1/3">{t.matrixCol1}</th>
                          <th className="p-3 w-1/3 text-rose-400">{t.matrixCol2}</th>
                          <th className="p-3 w-1/3 text-[#0E93A3]">{t.matrixCol3}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#262C31] bg-[#05070A]">
                        {t.matrixRows.map((row, idx) => (
                          <tr key={idx} className="hover:bg-[#12161A]/50 transition-colors">
                            <td className="p-3 font-mono font-medium text-[#EDF0F1]">{row.cap}</td>
                            <td className="p-3 text-slate-400 leading-relaxed">{row.bad}</td>
                            <td className="p-3 text-[#EDF0F1] font-medium leading-relaxed bg-[#0E93A3]/5">
                              {row.good}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>

              {/* CHAPTER 03: SOVEREIGN GEOSPATIAL INTELLIGENCE */}
              <section className="p-8 md:p-10 rounded-xl bg-[#12161A] border border-[#262C31] space-y-6">
                <div className="space-y-2">
                  <span className="text-[11px] font-mono font-bold text-[#0E93A3] uppercase tracking-[0.2em]">
                    {t.ch3Tag}
                  </span>
                  <h2
                    className="text-2xl md:text-3xl font-medium text-[#EDF0F1] tracking-[-0.4px] leading-tight"
                    style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
                  >
                    {t.ch3Title}
                  </h2>
                </div>

                <p className="text-base text-[#EDF0F1] leading-relaxed font-medium border-l-2 border-[#0E93A3] pl-4 italic">
                  {t.ch3Lead}
                </p>

                <div className="space-y-4 text-sm text-[#9AA5AC] leading-relaxed">
                  <p>{t.ch3P1}</p>
                  <p>{t.ch3P2}</p>
                  <p>{t.ch3P3}</p>
                </div>

                {/* Sovereign Guarantees Badges */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                  <div className="p-3.5 rounded-lg bg-[#05070A] border border-[#262C31] space-y-1">
                    <div className="flex items-center gap-2 text-[#0E93A3] text-xs font-semibold">
                      <Lock size={14} />
                      <span>Zero-Egress</span>
                    </div>
                    <p className="text-[11px] text-[#9AA5AC]">No telemetry or customer layers traverse external clouds.</p>
                  </div>

                  <div className="p-3.5 rounded-lg bg-[#05070A] border border-[#262C31] space-y-1">
                    <div className="flex items-center gap-2 text-[#0E93A3] text-xs font-semibold">
                      <Cpu size={14} />
                      <span>Air-Gapped Ready</span>
                    </div>
                    <p className="text-[11px] text-[#9AA5AC]">Self-contained deployment within private on-prem enclaves.</p>
                  </div>

                  <div className="p-3.5 rounded-lg bg-[#05070A] border border-[#262C31] space-y-1">
                    <div className="flex items-center gap-2 text-[#0E93A3] text-xs font-semibold">
                      <FileCheck2 size={14} />
                      <span>Non-Training</span>
                    </div>
                    <p className="text-[11px] text-[#9AA5AC]">Customer datasets are never used to train public weights.</p>
                  </div>
                </div>
              </section>

              {/* ─── Sovereign Intellectual Property Trust Card ───────────────────── */}
              <div className="p-6 md:p-8 rounded-xl bg-[#12161A] border border-[#262C31] flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#05070A] border border-[#262C31] flex items-center justify-center text-[#0E93A3] shrink-0 mt-1">
                    <ShieldCheck size={24} />
                  </div>
                  <div className="space-y-1">
                    <div className="text-[10px] font-mono text-[#0E93A3] uppercase tracking-widest font-bold">
                      {t.ipBadge}
                    </div>
                    <h4 className="text-base md:text-lg font-bold text-[#EDF0F1]">
                      {t.ipTitle}
                    </h4>
                    <p className="text-xs text-[#9AA5AC] leading-relaxed">
                      {t.ipDesc}
                    </p>
                  </div>
                </div>

                <Link
                  to="/intellectual-property"
                  className="shrink-0 px-5 py-2.5 rounded-full bg-[#0E93A3] hover:bg-[#075C68] text-white text-xs font-semibold tracking-wide transition-all shadow-[0_0_15px_rgba(14,147,163,0.25)] flex items-center gap-2"
                >
                  <span>{t.ipVerifyBtn}</span>
                  <ChevronRight size={14} className={isRtl ? 'rotate-180' : ''} />
                </Link>
              </div>

              {/* ─── Case Study Spotlight Card (Clean V2) ─────────────────────────── */}
              {content?.impact?.caseStudy && (
                <div className="p-8 rounded-xl bg-[#12161A] border border-[#262C31] flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                  <div className="space-y-2 max-w-xl">
                    <span className="text-[10px] font-mono text-[#0E93A3] uppercase tracking-wider font-bold">
                      {t.caseStudyBadge}
                    </span>
                    <h3 className="text-lg md:text-xl font-bold text-[#EDF0F1]">
                      {content.impact.caseStudy.title}
                    </h3>
                    <p className="text-xs md:text-sm text-[#9AA5AC] leading-relaxed">
                      {content.impact.caseStudy.desc}
                    </p>
                  </div>
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="shrink-0 px-5 py-2.5 rounded-full bg-[#171C21] hover:bg-[#262C31] text-[#EDF0F1] border border-[#262C31] hover:border-[#0E93A3] text-xs font-medium tracking-wide transition-all cursor-pointer"
                  >
                    {t.caseStudyBtn}
                  </button>
                </div>
              )}

            </main>
          </div>
        </div>

        {/* Case Study Modal Dialog */}
        {content?.caseStudy && (
          <CaseStudyModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            content={content.caseStudy}
            isRtl={isRtl}
          />
        )}
      </div>
    </PageTransition>
  );
};

export default Vision;
